// ==========================================
// FIREBASE SERVICES - Allo Artisan
// ==========================================
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  writeBatch,
  increment,
  onSnapshot,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import {
  listAll,
} from "firebase/storage";
import { db, storage } from "@/lib/firebase";
import { cloudinaryService } from "@/services/cloudinary";
import type {
  BaseEntity,
  HeroSection,
  ProblemSection,
  TimelineEvent,
  Feature,
  HowItWorksSection,
  MissionVision,
  Value,
  Service,
  City,
  TeamMember,
  Testimonial,
  FAQ,
  ContactMessage,
  ContactFormData,
  Artisan,
  ArtisanFormData,
  SocialLink,
  FooterSection,
  LiveStats,
  MediaFile,
  Admin,
  SiteSettings,
  DashboardStats,
} from "@/types";

// ==========================================
// UTILITAIRES
// ==========================================

const convertTimestamp = (data: DocumentData): DocumentData => {
  const converted = { ...data };
  if (data.createdAt instanceof Timestamp) {
    converted.createdAt = data.createdAt.toDate();
  }
  if (data.updatedAt instanceof Timestamp) {
    converted.updatedAt = data.updatedAt.toDate();
  }
  if (data.verifiedAt instanceof Timestamp) {
    converted.verifiedAt = data.verifiedAt.toDate();
  }
  if (data.suspendedAt instanceof Timestamp) {
    converted.suspendedAt = data.suspendedAt.toDate();
  }
  if (data.lastUpdated instanceof Timestamp) {
    converted.lastUpdated = data.lastUpdated.toDate();
  }
  if (data.lastLoginAt instanceof Timestamp) {
    converted.lastLoginAt = data.lastLoginAt.toDate();
  }
  return converted;
};

const docToEntity = <T extends BaseEntity>(doc: QueryDocumentSnapshot<DocumentData>): T => {
  const data = convertTimestamp(doc.data());
  return { id: doc.id, ...data } as T;
};

const getTimestamp = () => Timestamp.now();

// ==========================================
// COLLECTION REFERENCES
// ==========================================

const collections = {
  admins: () => collection(db, "admins"),
  siteSettings: () => collection(db, "siteSettings"),
  heroSection: () => collection(db, "heroSection"),
  problemSection: () => collection(db, "problemSection"),
  timeline: () => collection(db, "timeline"),
  features: () => collection(db, "features"),
  howItWorks: () => collection(db, "howItWorks"),
  missionVision: () => collection(db, "missionVision"),
  values: () => collection(db, "values"),
  services: () => collection(db, "services"),
  cities: () => collection(db, "cities"),
  teamMembers: () => collection(db, "teamMembers"),
  testimonials: () => collection(db, "testimonials"),
  faq: () => collection(db, "faq"),
  contactMessages: () => collection(db, "contactMessages"),
  artisans: () => collection(db, "artisans"),
  socialLinks: () => collection(db, "socialLinks"),
  footer: () => collection(db, "footer"),
  liveStats: () => collection(db, "liveStats"),
  media: () => collection(db, "media"),
};

// ==========================================
// CRUD GÉNÉRIQUE
// ==========================================

class GenericService<T extends BaseEntity> {
  constructor(private collectionName: keyof typeof collections) {}

  private getCol() {
    return collections[this.collectionName]();
  }

  async getAll(orderByField = "order", orderDirection: "asc" | "desc" = "asc"): Promise<T[]> {
    const q = query(this.getCol(), orderBy(orderByField, orderDirection));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<T>);
  }

  async getById(id: string): Promise<T | null> {
    const docRef = doc(db, this.collectionName as string, id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return docToEntity<T>(snapshot);
  }

  async getActive(orderByField = "order"): Promise<T[]> {
    const q = query(this.getCol(), where("active", "==", true), orderBy(orderByField, "asc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<T>);
  }

  async create(data: Omit<T, "id">): Promise<T> {
    const docRef = await addDoc(this.getCol(), {
      ...data,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
      active: true,
    });
    return { id: docRef.id, ...data, active: true } as T;
  }

  async update(id: string, data: Partial<T>): Promise<void> {
    const docRef = doc(db, this.collectionName as string, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: getTimestamp(),
    });
  }

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, this.collectionName as string, id));
  }

  async toggleActive(id: string, currentActive: boolean): Promise<void> {
    await this.update(id, { active: !currentActive } as Partial<T>);
  }

  // Real-time subscription
  subscribe(callback: (data: T[]) => void, orderByField = "order") {
    const q = query(this.getCol(), orderBy(orderByField, "asc"));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(docToEntity<T>);
      callback(data);
    });
  }

  subscribeActive(callback: (data: T[]) => void, orderByField = "order") {
    const q = query(this.getCol(), where("active", "==", true), orderBy(orderByField, "asc"));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(docToEntity<T>);
      callback(data);
    });
  }
}

// ==========================================
// SERVICES SPÉCIALISÉS
// ==========================================

export const heroService = new GenericService<HeroSection>("heroSection");
export const problemService = new GenericService<ProblemSection>("problemSection");
export const timelineService = new GenericService<TimelineEvent>("timeline");
export const featureService = new GenericService<Feature>("features");
export const howItWorksService = new GenericService<HowItWorksSection>("howItWorks");
export const missionVisionService = new GenericService<MissionVision>("missionVision");
export const valueService = new GenericService<Value>("values");
export const serviceService = new GenericService<Service>("services");
export const cityService = new GenericService<City>("cities");
export const teamMemberService = new GenericService<TeamMember>("teamMembers");
export const testimonialService = new GenericService<Testimonial>("testimonials");
export const faqService = new GenericService<FAQ>("faq");
export const socialLinkService = new GenericService<SocialLink>("socialLinks");
export const footerService = new GenericService<FooterSection>("footer");
export const mediaService = new GenericService<MediaFile>("media");

// ==========================================
// CONTACT MESSAGES SERVICE
// ==========================================

export const contactMessageService = {
  async create(data: ContactFormData): Promise<ContactMessage> {
    const docRef = await addDoc(collections.contactMessages(), {
      ...data,
      status: "new",
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
      active: true,
    });
    return {
      id: docRef.id,
      ...data,
      status: "new",
      active: true,
    } as ContactMessage;
  },

  async getAll(status?: string): Promise<ContactMessage[]> {
    let q = query(collections.contactMessages(), orderBy("createdAt", "desc"));
    if (status) {
      q = query(collections.contactMessages(), where("status", "==", status), orderBy("createdAt", "desc"));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<ContactMessage>);
  },

  async updateStatus(id: string, status: ContactMessage["status"], notes?: string): Promise<void> {
    const updateData: Record<string, unknown> = { status, updatedAt: getTimestamp() };
    if (notes) updateData.notes = notes;
    if (status === "replied") updateData.repliedAt = getTimestamp();
    await updateDoc(doc(db, "contactMessages", id), updateData);
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, "contactMessages", id));
  },

  subscribe(callback: (messages: ContactMessage[]) => void) {
    const q = query(collections.contactMessages(), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(docToEntity<ContactMessage>));
    });
  },

  subscribeUnread(callback: (count: number) => void) {
    const q = query(collections.contactMessages(), where("status", "==", "new"));
    return onSnapshot(q, (snapshot) => callback(snapshot.size));
  },
};

// ==========================================
// ARTISAN SERVICE
// ==========================================

export const artisanService = {
  async create(data: ArtisanFormData): Promise<Artisan> {
    const docRef = await addDoc(collections.artisans(), {
      ...data,
      status: "pending",
      rating: 0,
      reviewCount: 0,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
      active: true,
    });
    return {
      id: docRef.id,
      ...data,
      status: "pending",
      rating: 0,
      reviewCount: 0,
      active: true,
    } as Artisan;
  },

  async getAll(): Promise<Artisan[]> {
    const q = query(collections.artisans(), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<Artisan>);
  },

  async getByStatus(status: Artisan["status"]): Promise<Artisan[]> {
    const q = query(collections.artisans(), where("status", "==", status), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<Artisan>);
  },

  async getByService(serviceSlug: string): Promise<Artisan[]> {
    const q = query(
      collections.artisans(),
      where("serviceSlug", "==", serviceSlug),
      where("status", "==", "verified"),
      orderBy("rating", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<Artisan>);
  },

  async getByCity(citySlug: string): Promise<Artisan[]> {
    const q = query(
      collections.artisans(),
      where("citySlug", "==", citySlug),
      where("status", "==", "verified"),
      orderBy("rating", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<Artisan>);
  },

  async update(id: string, data: Partial<Artisan>): Promise<void> {
    await updateDoc(doc(db, "artisans", id), {
      ...data,
      updatedAt: getTimestamp(),
    });
  },

  async verify(id: string, adminId: string): Promise<void> {
    await updateDoc(doc(db, "artisans", id), {
      status: "verified",
      verifiedAt: getTimestamp(),
      verifiedBy: adminId,
      updatedAt: getTimestamp(),
    });
  },

  async suspend(id: string, reason: string): Promise<void> {
    await updateDoc(doc(db, "artisans", id), {
      status: "suspended",
      suspendedAt: getTimestamp(),
      suspendedReason: reason,
      updatedAt: getTimestamp(),
    });
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, "artisans", id));
  },

  async toggleActive(id: string, currentActive: boolean): Promise<void> {
    await updateDoc(doc(db, "artisans", id), {
      active: !currentActive,
      updatedAt: getTimestamp(),
    });
  },

  subscribe(callback: (artisans: Artisan[]) => void) {
    const q = query(collections.artisans(), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(docToEntity<Artisan>));
    });
  },

  subscribePending(callback: (count: number) => void) {
    const q = query(collections.artisans(), where("status", "==", "pending"));
    return onSnapshot(q, (snapshot) => callback(snapshot.size));
  },
};

// ==========================================
// ADMIN SERVICE
// ==========================================

export const adminService = {
  async create(data: Omit<Admin, "id" | "createdAt" | "updatedAt">): Promise<Admin> {
    const docRef = await addDoc(collections.admins(), {
      ...data,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
      active: true,
    });
    return { id: docRef.id, ...data, active: true } as Admin;
  },

  async getAll(): Promise<Admin[]> {
    const q = query(collections.admins(), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToEntity<Admin>);
  },

  async getByUid(uid: string): Promise<Admin | null> {
    const q = query(collections.admins(), where("uid", "==", uid), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return docToEntity<Admin>(snapshot.docs[0]);
  },

  async update(id: string, data: Partial<Admin>): Promise<void> {
    await updateDoc(doc(db, "admins", id), {
      ...data,
      updatedAt: getTimestamp(),
    });
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, "admins", id));
  },
};

// ==========================================
// SITE SETTINGS SERVICE
// ==========================================

export const siteSettingsService = {
  async get(): Promise<SiteSettings | null> {
    const q = query(collections.siteSettings(), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return docToEntity<SiteSettings>(snapshot.docs[0]);
  },

  async update(id: string, data: Partial<SiteSettings>): Promise<void> {
    await updateDoc(doc(db, "siteSettings", id), {
      ...data,
      updatedAt: getTimestamp(),
    });
  },

  subscribe(callback: (settings: SiteSettings | null) => void) {
    const q = query(collections.siteSettings(), limit(1));
    return onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(null);
      } else {
        callback(docToEntity<SiteSettings>(snapshot.docs[0]));
      }
    });
  },
};

// ==========================================
// LIVE STATS SERVICE
// ==========================================

export const liveStatsService = {
  async get(): Promise<LiveStats | null> {
    const q = query(collections.liveStats(), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return docToEntity<LiveStats>(snapshot.docs[0]);
  },

  async update(id: string, data: Partial<LiveStats>): Promise<void> {
    await updateDoc(doc(db, "liveStats", id), {
      ...data,
      lastUpdated: getTimestamp(),
    });
  },

  subscribe(callback: (stats: LiveStats | null) => void) {
    const q = query(collections.liveStats(), limit(1));
    return onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(null);
      } else {
        callback(docToEntity<LiveStats>(snapshot.docs[0]));
      }
    });
  },
};

// ==========================================
// DASHBOARD STATS
// ==========================================

export const dashboardService = {
  async getStats(): Promise<DashboardStats> {
    const [artisansSnap, pendingSnap, messagesSnap, unreadSnap, testimonialsSnap, servicesSnap, citiesSnap] = await Promise.all([
      getDocs(query(collections.artisans())),
      getDocs(query(collections.artisans(), where("status", "==", "pending"))),
      getDocs(query(collections.contactMessages())),
      getDocs(query(collections.contactMessages(), where("status", "==", "new"))),
      getDocs(query(collections.testimonials())),
      getDocs(query(collections.services())),
      getDocs(query(collections.cities())),
    ]);

    const recentMessagesQ = query(collections.contactMessages(), orderBy("createdAt", "desc"), limit(5));
    const recentMessagesSnap = await getDocs(recentMessagesQ);

    const recentArtisansQ = query(collections.artisans(), orderBy("createdAt", "desc"), limit(5));
    const recentArtisansSnap = await getDocs(recentArtisansQ);

    return {
      totalArtisans: artisansSnap.size,
      pendingArtisans: pendingSnap.size,
      verifiedArtisans: artisansSnap.size - pendingSnap.size,
      totalMessages: messagesSnap.size,
      unreadMessages: unreadSnap.size,
      totalTestimonials: testimonialsSnap.size,
      totalServices: servicesSnap.size,
      totalCities: citiesSnap.size,
      recentMessages: recentMessagesSnap.docs.map(docToEntity<ContactMessage>),
      recentArtisans: recentArtisansSnap.docs.map(docToEntity<Artisan>),
      chartData: {
        labels: ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin"],
        artisans: [12, 19, 25, 32, 45, 58],
        messages: [8, 15, 22, 28, 35, 42],
      },
    };
  },
};

// ==========================================
// STORAGE SERVICE
// ==========================================

export const storageService = {
  async uploadFile(file: File, folder: string): Promise<MediaFile> {
    const mediaData = await cloudinaryService.uploadFile(file, folder, [folder, "allo-artisan"]);
    return mediaService.create(mediaData);
  },

  async deleteFile(mediaId: string, media: Pick<MediaFile, "publicId" | "resourceType">): Promise<void> {
    if (media.publicId) {
      try {
        await cloudinaryService.deleteFile(media);
      } catch (error) {
        console.error("Erreur suppression Cloudinary:", error);
      }
    }
    await mediaService.delete(mediaId);
  },

  async listFiles(folder: string): Promise<MediaFile[]> {
    const { ref, getDownloadURL } = await import("firebase/storage");
    const listRef = ref(storage, folder);
    const result = await listAll(listRef);
    
    const files = await Promise.all(
      result.items.map(async (item) => {
        const url = await getDownloadURL(item);
        return {
          id: item.name,
          name: item.name,
          url,
          path: item.fullPath,
          size: 0,
          type: "",
          folder,
          createdAt: new Date(),
          updatedAt: new Date(),
          active: true,
        } as MediaFile;
      })
    );
    
    return files;
  },
};

// ==========================================
// SEED DATA (Initialisation)
// ==========================================

export const seedData = {
  async initialize(): Promise<void> {
    const batch = writeBatch(db);
    
    // Check if data already exists
    const heroSnap = await getDocs(query(collections.heroSection(), limit(1)));
    if (!heroSnap.empty) {
      console.log("Data already seeded");
      return;
    }

    // Hero Section
    const heroRef = doc(collections.heroSection());
    batch.set(heroRef, {
      title: "Trouvez un artisan de confiance en 5 minutes.",
      subtitle: "Plombiers, électriciens, mécaniciens, peintres, menuisiers... disponibles près de chez vous à Abidjan.",
      description: "Allo Artisan connecte instantanément les particuliers et les entreprises avec des artisans qualifiés, vérifiés et géolocalisés. Fini les heures perdues à chercher par le bouche-à-oreille. En quelques clics, votre artisan arrive.",
      ctaClientText: "Je suis Client — Je trouve un artisan",
      ctaClientLink: "#services",
      ctaArtisanText: "Je suis Artisan — Je rejoins la plateforme",
      ctaArtisanLink: "#contact",
      stats: [
        { id: "1", value: "25+", label: "Artisans vérifiés", icon: "users" },
        { id: "2", value: "5", label: "Métiers disponibles", icon: "briefcase" },
        { id: "3", value: "3", label: "Temps de réponse moyen", suffix: "min", icon: "clock" },
        { id: "4", value: "100", label: "Artisans vérifiés manuellement", suffix: "%", icon: "shield-check" },
      ],
      active: true,
      order: 1,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
    });

    // Problem Section
    const problemRef = doc(collections.problemSection());
    batch.set(problemRef, {
      title: "Un problème quotidien qui touche des millions de personnes.",
      subtitle: "Vous avez déjà vécu l'une de ces situations ?",
      scenarios: [
        { id: "1", icon: "droplet", title: "Il est 22h. Votre robinet fuit.", description: "Vous appelez un ami, il appelle un cousin, le cousin connaît quelqu'un... Le lendemain matin, votre cuisine est inondée. Aucun plombier disponible, aucun contact fiable, aucun recours.", emoji: "💧" },
        { id: "2", icon: "zap", title: "Votre disjoncteur a sauté.", description: "Vous cherchez un électricien depuis 3 jours. On vous donne plusieurs numéros, certains ne répondent pas, d'autres demandent des tarifs exorbitants, un ne s'est jamais présenté au rendez-vous.", emoji: "⚡" },
        { id: "3", icon: "home", title: "Vous venez d'emménager.", description: "La porte de votre chambre ne ferme pas, la climatisation tombe en panne, la peinture s'écaille. Trouver le bon artisan pour chaque besoin devient un véritable parcours du combattant.", emoji: "🔧" },
      ],
      narrativeText: "En Côte d'Ivoire, le secteur de l'artisanat représente des centaines de milliers d'emplois et répond à des besoins quotidiens essentiels. Pourtant, la mise en relation entre clients et artisans reste profondément archaïque, reposant presque exclusivement sur le bouche-à-oreille, les recommandations personnelles et le hasard.\n\nCette situation génère une double souffrance : d'un côté, des clients qui perdent un temps précieux, s'exposent à des arnaques et ne peuvent ni comparer les offres ni évaluer la qualité avant de s'engager. De l'autre, des artisans compétents et sérieux qui manquent de visibilité, peinent à développer leur clientèle et ne disposent d'aucun outil pour structurer et faire grandir leur activité.\n\nCe constat, nous l'avons vécu personnellement, nous l'avons observé autour de nous, et nous avons décidé d'en faire l'objet d'un projet concret, utile, et à fort impact social.",
      stats: [
        { id: "1", value: "90%", label: "Des recherches d'artisans reposent encore sur le bouche-à-oreille", description: "" },
        { id: "2", value: "3 à 5 jours", label: "Délai moyen pour trouver un artisan disponible à Abidjan", description: "" },
        { id: "3", value: "0", label: "Aucun mécanisme de garantie en cas de mauvaise prestation", description: "" },
      ],
      active: true,
      order: 1,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
    });

    // Timeline
    const timelineEvents = [
      { year: "2025", month: "", title: "Identification du problème", description: "L'équipe observe et documente la désorganisation du marché artisanal ivoirien.", icon: "search", highlight: false },
      { year: "2026", month: "Jan.", title: "Naissance officielle du projet", description: "Constitution de l'équipe fondatrice. Définition de la vision et de la mission.", icon: "rocket", highlight: true },
      { year: "2026", month: "Fév.", title: "Études de marché", description: "Interviews d'artisans et de clients, définition du modèle économique. Premières maquettes de l'application.", icon: "clipboard-list", highlight: false },
      { year: "2026", month: "Avr.", title: "Lancement du développement", description: "Début du recrutement des premiers artisans pilotes à Abidjan (Cocody, Yopougon, Marcory).", icon: "code", highlight: false },
      { year: "2026", month: "Mai", title: "Lancement du prototype", description: "Allo Artisan est disponible à Abidjan pour les premiers utilisateurs.", icon: "rocket", highlight: true },
    ];
    
    timelineEvents.forEach((event, index) => {
      const ref = doc(collections.timeline());
      batch.set(ref, { ...event, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Features
    const features = [
      { icon: "search", title: "Recherche instantanée", description: "Trouvez un artisan par métier et par commune en quelques secondes. Filtrez par disponibilité, note ou tarif.", color: "#E8671A", highlight: true },
      { icon: "shield-check", title: "Artisans vérifiés", description: "Chaque artisan est contrôlé par notre équipe terrain : identité, compétences, références. Aucun artisan non validé n'apparaît.", color: "#0066CC", highlight: true },
      { icon: "star", title: "Avis clients vérifiés", description: "Les notes et commentaires proviennent uniquement de clients ayant réellement utilisé le service. Aucune manipulation possible.", color: "#E8671A", highlight: false },
      { icon: "phone", title: "Contact direct", description: "Appelez ou écrivez à l'artisan directement via WhatsApp d'un simple tap. Aucun intermédiaire, aucune attente.", color: "#0066CC", highlight: false },
      { icon: "smartphone", title: "Application mobile", description: "Disponible sur Android. Optimisée pour les connexions 3G et les téléphones d'entrée de gamme courants en Côte d'Ivoire.", color: "#1C2340", highlight: false },
      { icon: "globe", title: "Version web accessible", description: "Pas besoin d'installer l'app. Accédez à Allo Artisan.ci depuis n'importe quel navigateur sur téléphone ou ordinateur.", color: "#1C2340", highlight: false },
    ];
    
    features.forEach((feature, index) => {
      const ref = doc(collections.features());
      batch.set(ref, { ...feature, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // How It Works
    const howItWorksRef = doc(collections.howItWorks());
    batch.set(howItWorksRef, {
      title: "Simple. Rapide. Efficace.",
      subtitle: "Allo Artisan en 3 étapes, que vous soyez client ou artisan.",
      clientSteps: [
        { id: "1", number: 1, title: "Cherchez", description: "Sélectionnez votre commune et le type d'artisan dont vous avez besoin. La liste des disponibles s'affiche immédiatement.", icon: "search" },
        { id: "2", number: 2, title: "Choisissez", description: "Consultez les profils, les notes et les avis clients. Comparez et choisissez l'artisan qui vous convient.", icon: "user-check" },
        { id: "3", number: 3, title: "Contactez", description: "Appelez directement ou envoyez un message WhatsApp. L'artisan vous répond et fixe le rendez-vous. C'est tout.", icon: "phone" },
      ],
      artisanSteps: [
        { id: "1", number: 1, title: "Inscrivez-vous", description: "Créez votre profil professionnel : photo, métier, zone d'intervention, tarifs. Notre équipe valide votre inscription sous 48h.", icon: "file-text" },
        { id: "2", number: 2, title: "Recevez", description: "Recevez des notifications de demandes de clients dans votre zone. Consultez les détails et acceptez ou refusez selon votre disponibilité.", icon: "bell" },
        { id: "3", number: 3, title: "Grandissez", description: "Accumulez les missions réalisées, recevez des avis clients, améliorez votre note et construisez une réputation solide qui attire encore plus de clients.", icon: "trending-up" },
      ],
      active: true,
      order: 1,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
    });

    // Mission & Vision
    const missionRef = doc(collections.missionVision());
    batch.set(missionRef, {
      missionTitle: "Notre Mission",
      missionText: "Rendre accessible à tous les Ivoiriens — particuliers, locataires, propriétaires et entreprises — un service de mise en relation rapide, transparent et fiable avec des artisans qualifiés, tout en offrant à ces artisans une plateforme professionnelle pour développer leur activité, améliorer leur visibilité et accroître leurs revenus.",
      missionIcon: "target",
      visionTitle: "Notre Vision",
      visionText: "Devenir la référence incontournable du secteur artisanal en Afrique de l'Ouest : une plateforme qui structure et valorise le travail artisanal informel, contribue à la création d'emplois durables, et démontre que des startups nées en Côte d'Ivoire peuvent résoudre des problèmes africains à grande échelle.",
      visionIcon: "globe",
      active: true,
      order: 1,
      createdAt: getTimestamp(),
      updatedAt: getTimestamp(),
    });

    // Values
    const values = [
      { icon: "handshake", title: "Confiance", description: "Chaque artisan est vérifié. Chaque avis est authentique. Nous construisons un espace où la confiance n'est pas un luxe, mais un standard.", color: "#E8671A" },
      { icon: "zap", title: "Rapidité", description: "En Côte d'Ivoire, le temps est précieux. Nous nous engageons à connecter un client à un artisan en moins de 5 minutes.", color: "#0066CC" },
      { icon: "sprout", title: "Impact social", description: "Chaque mission réalisée via Allo Artisan, c'est un artisan qui gagne mieux sa vie et une famille qui vit dans de meilleures conditions.", color: "#22C55E" },
      { icon: "lock", title: "Transparence", description: "Profils vérifiés, avis réels, tarifs indicatifs affichés. Nous mettons fin à l'opacité qui gangrène le secteur artisanal.", color: "#1C2340" },
      { icon: "smartphone", title: "Accessibilité", description: "Pensée pour tous : connexion 3G, anciens téléphones, faible alphabétisation numérique. Allo Artisan est simple par conviction.", color: "#0066CC" },
      { icon: "award", title: "Excellence", description: "Nous sélectionnons les meilleurs artisans et les aidons à progresser. Notre réputation est leur réputation.", color: "#E8671A" },
    ];
    
    values.forEach((value, index) => {
      const ref = doc(collections.values());
      batch.set(ref, { ...value, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Services
    const services = [
      { icon: "wrench", name: "Plomberie", slug: "plomberie", description: "Réparation fuite, installation sanitaire, débouchage", color: "#3B82F6" },
      { icon: "zap", name: "Électricité", slug: "electricite", description: "Installation, réparation, mise aux normes", color: "#F59E0B" },
      { icon: "hammer", name: "Menuiserie", slug: "menuiserie", description: "Portes, fenêtres, meubles sur mesure", color: "#8B5CF6" },
      { icon: "paintbrush", name: "Peinture", slug: "peinture", description: "Intérieur, extérieur, décoration", color: "#EC4899" },
      { icon: "car", name: "Mécanique auto", slug: "mecanique", description: "Entretien, réparation, diagnostic", color: "#EF4444" },
      { icon: "snowflake", name: "Climatisation", slug: "climatisation", description: "Installation, entretien, réparation", color: "#06B6D4" },
      { icon: "sparkles", name: "Ménage & nettoyage", slug: "menage", description: "Nettoyage régulier, grand ménage, vitres", color: "#10B981" },
      { icon: "flower-2", name: "Jardinage", slug: "jardinage", description: "Entretien espaces verts, taille, plantation", color: "#84CC16" },
      { icon: "tool", name: "Travaux divers", slug: "travaux", description: "Petits travaux, montage, réparations diverses", color: "#6366F1" },
    ];
    
    services.forEach((service, index) => {
      const ref = doc(collections.services());
      batch.set(ref, { ...service, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Cities
    const cities = [
      { name: "Cocody", slug: "cocody", icon: "map-pin" },
      { name: "Yopougon", slug: "yopougon", icon: "map-pin" },
      { name: "Marcory", slug: "marcory", icon: "map-pin" },
      { name: "Plateau", slug: "plateau", icon: "map-pin" },
      { name: "Adjamé", slug: "adjame", icon: "map-pin" },
      { name: "Abobo", slug: "abobo", icon: "map-pin" },
      { name: "Koumassi", slug: "koumassi", icon: "map-pin" },
      { name: "Treichville", slug: "treichville", icon: "map-pin" },
      { name: "Port-Bouët", slug: "port-bouet", icon: "map-pin" },
    ];
    
    cities.forEach((city, index) => {
      const ref = doc(collections.cities());
      batch.set(ref, { ...city, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Team Members
    const teamMembers = [
      {
        name: "Rodrigue Bazemo",
        role: "Co-fondateur — Marketing & Finance",
        bio: "Rodrigue porte la vision commerciale et financière du projet. Spécialisé en finance et marketing, il est responsable de la stratégie de croissance, des relations investisseurs et de la communication d'Allo Artisan. Passionné par l'entrepreneuriat et l'impact social, il croit fermement que la technologie peut transformer le quotidien des Ivoiriens.",
        phone: "+225 01 42 20 46 01",
        email: "rodrigue@alloartisan.ci",
        skills: ["Finance", "Marketing", "Stratégie", "Relations investisseurs"],
        order: 1,
      },
      {
        name: "Maximin",
        role: "Co-fondateur — Informatique & Dev",
        bio: "Informaticien de formation, Maximin est l'architecte technique d'Allo Artisan. Il conçoit et développe l'application mobile et la plateforme web, en veillant à ce que la technologie reste accessible, rapide et fiable même dans les conditions d'infrastructure numérique du marché ivoirien.",
        phone: "",
        email: "maximin@alloartisan.ci",
        skills: ["Développement", "Architecture", "DevOps", "UI/UX"],
        order: 2,
      },
      {
        name: "Boga",
        role: "Co-fondateur — Logistique & Terrain",
        bio: "Spécialiste en logistique, Boga est le pilier opérationnel de l'équipe. Il gère le recrutement et la vérification des artisans sur le terrain, les opérations de déploiement dans les quartiers d'Abidjan, et s'assure que la qualité de service est au rendez-vous à chaque mission.",
        phone: "",
        email: "boga@alloartisan.ci",
        skills: ["Logistique", "Opérations", "Recrutement", "Qualité"],
        order: 3,
      },
    ];
    
    teamMembers.forEach((member) => {
      const ref = doc(collections.teamMembers());
      batch.set(ref, { ...member, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Testimonials
    const testimonials = [
      { name: "Aya Konan", role: "Cliente", location: "Cocody", rating: 5, text: "J'ai trouvé un plombier en 10 minutes un samedi soir. Il était là en une heure. Du jamais vu à Abidjan ! Je ne chercherai plus jamais autrement.", type: "client" as const, verified: true, featured: true },
      { name: "Konan Yao Marcel", role: "Plombier", location: "Cocody", rating: 5, text: "Depuis que j'ai rejoint Allo Artisan, j'ai 3 fois plus de clients. Je n'ai plus besoin d'attendre que les gens me recommandent. Les missions viennent à moi.", type: "artisan" as const, verified: true, featured: true },
      { name: "Paul Touré", role: "Client", location: "Marcory", rating: 4, text: "Super application. J'ai pu comparer les artisans avant de choisir. Les avis des autres clients m'ont vraiment aidé. La prise de contact via WhatsApp est très pratique.", type: "client" as const, verified: true, featured: false },
      { name: "Mariam Coulibaly", role: "Cliente", location: "Yopougon", rating: 5, text: "J'ai demandé un électricien pour ma boutique. Professionnel, ponctuel, travail de qualité. Et la note que j'ai laissée aide d'autres personnes à faire le bon choix.", type: "client" as const, verified: true, featured: false },
      { name: "Bamba Drissa", role: "Électricien", location: "Adjamé", rating: 5, text: "Allo Artisan a changé ma vie professionnelle. J'ai maintenant un profil visible, des clients qui connaissent mes tarifs, et une réputation qui se construit avec le temps.", type: "artisan" as const, verified: true, featured: true },
      { name: "Koffi Assi", role: "Client", location: "Abobo", rating: 4, text: "Facile à utiliser même sur mon vieux téléphone. En 3 clics j'avais trouvé un menuisier dans mon quartier. Je recommande à toute ma famille.", type: "client" as const, verified: true, featured: false },
    ];
    
    testimonials.forEach((testimonial, index) => {
      const ref = doc(collections.testimonials());
      batch.set(ref, { ...testimonial, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // FAQ
    const faqs = [
      { question: "Allo Artisan est-il gratuit pour les clients ?", answer: "Oui, totalement. L'utilisation d'Allo Artisan est entièrement gratuite pour les clients : inscription, recherche, consultation des profils et prise de contact. Vous ne payez que l'artisan pour sa prestation, directement et au prix convenu entre vous.", category: "Général", order: 1 },
      { question: "Comment les artisans sont-ils sélectionnés ?", answer: "Chaque artisan inscrit sur Allo Artisan est vérifié manuellement par notre équipe terrain avant d'apparaître sur la plateforme : vérification d'identité, évaluation des compétences de base, et recueil de références. Les artisans non conformes sont rejetés ou suspendus.", category: "Artisans", order: 2 },
      { question: "Comment sont calculées les notes des artisans ?", answer: "Les notes sont calculées automatiquement à partir des avis laissés par les clients ayant réellement utilisé le service (avis vérifiés). Seuls les clients ayant finalisé une demande de service via Allo Artisan peuvent noter. Aucune manipulation n'est possible.", category: "Artisans", order: 3 },
      { question: "L'application fonctionne-t-elle sans bonne connexion internet ?", answer: "Allo Artisan est conçue pour fonctionner sur les réseaux 3G courants en Côte d'Ivoire. Elle est légère, rapide, et peut afficher les informations en cache lorsque la connexion est instable. Une connexion 4G n'est pas nécessaire.", category: "Technique", order: 4 },
      { question: "Dans quelles communes puis-je trouver des artisans ?", answer: "Allo Artisan couvre actuellement 9 communes d'Abidjan : Cocody, Yopougon, Marcory, Plateau, Adjamé, Abobo, Koumassi, Treichville et Port-Bouët. D'autres villes de Côte d'Ivoire (Bouaké, Yamoussoukro, San-Pédro) seront ajoutées prochainement.", category: "Zones", order: 5 },
      { question: "Je suis artisan. Comment rejoindre Allo Artisan ?", answer: "Téléchargez l'application ou rendez-vous sur notre site. Cliquez sur 'Je suis artisan', renseignez votre profil (métier, zone, photo, tarif), et soumettez votre inscription. Notre équipe terrain vous contactera sous 48 heures pour valider votre profil.", category: "Artisans", order: 6 },
      { question: "Que faire si je suis insatisfait de la prestation ?", answer: "Vous pouvez laisser une note et un commentaire sur le profil de l'artisan après chaque mission. Notre équipe support est disponible sur WhatsApp pour traiter les litiges. Un artisan avec une note trop basse est suspendu automatiquement de la plateforme.", category: "Support", order: 7 },
      { question: "Allo Artisan prend-il une commission sur mes paiements ?", answer: "Non, vous payez l'artisan directement et librement au prix convenu entre vous. Allo Artisan ne s'interpose pas dans la transaction financière entre vous et l'artisan. Nous sommes rémunérés directement par les artisans inscrits sur la plateforme.", category: "Paiement", order: 8 },
    ];
    
    faqs.forEach((faq) => {
      const ref = doc(collections.faq());
      batch.set(ref, { ...faq, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Social Links
    const socialLinks = [
      { platform: "facebook", url: "https://facebook.com/AlloArtisanCI", icon: "facebook", label: "Facebook", order: 1 },
      { platform: "instagram", url: "https://instagram.com/AlloArtisan_ci", icon: "instagram", label: "Instagram", order: 2 },
      { platform: "tiktok", url: "https://tiktok.com/@AlloArtisan", icon: "music", label: "TikTok", order: 3 },
      { platform: "whatsapp", url: "https://wa.me/2250142204601", icon: "message-circle", label: "WhatsApp", order: 4 },
    ];
    
    socialLinks.forEach((link) => {
      const ref = doc(collections.socialLinks());
      batch.set(ref, { ...link, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Footer
    const footerSections = [
      {
        column: "brand" as const,
        title: "",
        links: [
          { id: "1", label: "L'artisan de confiance, à portée de main.", url: "", external: false },
          { id: "2", label: "Abidjan, Côte d'Ivoire", url: "", external: false },
        ],
      },
      {
        column: "navigation" as const,
        title: "Navigation",
        links: [
          { id: "1", label: "Accueil", url: "/", external: false },
          { id: "2", label: "À propos", url: "#about", external: false },
          { id: "3", label: "Comment ça marche", url: "#how-it-works", external: false },
          { id: "4", label: "Nos artisans", url: "#services", external: false },
          { id: "5", label: "FAQ", url: "#faq", external: false },
          { id: "6", label: "Contact", url: "#contact", external: false },
        ],
      },
      {
        column: "artisans" as const,
        title: "Artisans",
        links: [
          { id: "1", label: "Devenir artisan", url: "#contact", external: false },
          { id: "2", label: "Connexion artisan", url: "#", external: false },
          { id: "3", label: "Guide artisan", url: "#", external: false },
          { id: "4", label: "Charte qualité", url: "#", external: false },
        ],
      },
      {
        column: "contact" as const,
        title: "Contact",
        links: [
          { id: "1", label: "+225 01 42 20 46 01", url: "tel:+2250142204601", external: true },
          { id: "2", label: "contact@AlloArtisan.ci", url: "mailto:contact@AlloArtisan.ci", external: true },
          { id: "3", label: "Facebook", url: "https://facebook.com/AlloArtisanCI", external: true },
          { id: "4", label: "Instagram", url: "https://instagram.com/AlloArtisan_ci", external: true },
          { id: "5", label: "TikTok", url: "https://tiktok.com/@AlloArtisan", external: true },
        ],
      },
    ];
    
    footerSections.forEach((section, index) => {
      const ref = doc(collections.footer());
      batch.set(ref, { ...section, order: index + 1, active: true, createdAt: getTimestamp(), updatedAt: getTimestamp() });
    });

    // Live Stats
    const statsRef = doc(collections.liveStats());
    batch.set(statsRef, {
      artisansCount: 25,
      servicesCount: 9,
      responseTime: "3",
      verifiedPercentage: 100,
      missionsCompleted: 156,
      citiesCount: 9,
      happyClients: 342,
      lastUpdated: getTimestamp(),
    });

    // Site Settings
    const settingsRef = doc(collections.siteSettings());
    batch.set(settingsRef, {
      siteName: "Allo Artisan",
      tagline: "L'artisan de confiance, à portée de main.",
      primaryColor: "#E8671A",
      secondaryColor: "#0066CC",
      darkColor: "#1C2340",
      contactPhone: "+225 01 42 20 46 01",
      contactEmail: "contact@AlloArtisan.ci",
      contactWhatsApp: "+225 01 42 20 46 01",
      contactAddress: "Abidjan, Côte d'Ivoire",
      metaTitle: "Allo Artisan — Trouvez un artisan de confiance en 5 minutes",
      metaDescription: "Allo Artisan connecte les particuliers et entreprises avec des artisans qualifiés et vérifiés à Abidjan. Plombiers, électriciens, mécaniciens, peintres, menuisiers.",
      metaKeywords: ["artisan", "Abidjan", "Côte d'Ivoire", "plombier", "électricien", "mécanicien", "menuisier", "peintre"],
      maintenanceMode: false,
      updatedAt: getTimestamp(),
    });

    await batch.commit();
    console.log("✅ Données initiales créées avec succès");
  },
};

export {
  collections,
  convertTimestamp,
  docToEntity,
  getTimestamp,
};
